/**
 * ReadyBriefsQueue — emails with a prepared brief the AM still needs to
 * review and start (monitored mailboxes, their own mailbox, or a brief they
 * prepared by opening the email). Distinct from WaitingQueue (in-flight HITL).
 * Shared by ora and the add-in.
 *
 * The card matches the current-email strip: white panel, recommended label,
 * Review & Approve. Ignore sits top-right. Open Email shares the recommended
 * label's row, right-aligned above Review & Approve, so the card spends no
 * extra line on it.
 */

import React from "react";
import { CONTEXT_PRIMARY_ACTION } from "../emailContextState";
import type { ReadyBriefItem } from "../types";
import { OpenEmailLink } from "./OpenEmailLink";

export const READY_BRIEFS_HEADING = "Ready to start";
export const READY_BRIEFS_EMPTY_COPY = "No briefs waiting to start";

export interface ReadyBriefsQueueProps {
  items: ReadyBriefItem[];
  loading?: boolean;
  /** RFC 5322 id of the email the pane is currently on — omitted from this list. */
  currentMessageId?: string | null;
  onOpenBrief: (item: ReadyBriefItem) => void;
  onOpenInOutlook?: (item: ReadyBriefItem) => void;
  /** Hide this brief from THIS AM's list. Does not delete the analysis. */
  onIgnore?: (item: ReadyBriefItem) => void;
}

function subjectLine(item: ReadyBriefItem): string {
  if (item.subject) return item.subject;
  if (item.client_name && item.policy_number) {
    return `${item.client_name} · ${item.policy_number}`;
  }
  if (item.client_name) return item.client_name;
  return "Brief ready";
}

function recommendedLabel(workflowId: string): string {
  return workflowId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ReadyBriefsQueue({
  items,
  loading = false,
  currentMessageId,
  onOpenBrief,
  onOpenInOutlook,
  onIgnore,
}: ReadyBriefsQueueProps): React.ReactElement {
  const current = (currentMessageId || "").trim();
  const visible = current
    ? items.filter((item) => item.internet_message_id !== current)
    : items;

  return (
    <section
      className="flex w-full flex-col gap-2"
      data-testid="ready-briefs-queue"
      data-item-count={visible.length}
    >
      <h3
        data-testid="ready-briefs-heading"
        className="mb-1 text-[10px] font-semibold uppercase tracking-[0.07em] text-primary-gray-light-500"
      >
        {READY_BRIEFS_HEADING} · {visible.length}
      </h3>
      {loading && visible.length === 0 ? (
        <p
          data-testid="ready-briefs-loading"
          className="px-3 py-3 text-center text-sm text-primary-gray-light-500"
        >
          Checking for briefs…
        </p>
      ) : visible.length === 0 ? (
        <p
          data-testid="ready-briefs-empty"
          className="rounded-[10px] border border-dashed border-primary-gray-light-200 px-3 py-3 text-center text-sm text-primary-gray-light-600"
        >
          {READY_BRIEFS_EMPTY_COPY}
        </p>
      ) : (
        <ul
          aria-label={READY_BRIEFS_HEADING}
          className="flex max-h-56 list-none flex-col gap-2 overflow-y-auto p-0"
        >
          {visible.map((item) => (
            <li key={item.internet_message_id} className="list-none">
              <div
                className="flex w-full flex-col gap-2 rounded-lg border border-primary-gray-light-300 bg-base-white px-3 py-3"
                data-testid="ready-brief-row"
                data-message-id={item.internet_message_id}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold leading-[19px] text-primary-gray-light-900">
                      {subjectLine(item)}
                    </p>
                    {item.sender ? (
                      <p className="text-2xs leading-4 text-primary-gray-light-600">
                        {item.sender}
                      </p>
                    ) : null}
                    {item.client_name || item.policy_number ? (
                      <p className="text-2xs leading-4 text-primary-gray-light-600">
                        {[item.client_name, item.policy_number]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    ) : null}
                  </div>
                  {onIgnore ? (
                    <button
                      type="button"
                      onClick={() => onIgnore(item)}
                      data-testid="ready-brief-ignore"
                      className="flex-shrink-0 text-xs font-medium text-primary-gray-light-500 hover:text-primary-gray-light-800"
                    >
                      Ignore
                    </button>
                  ) : null}
                </div>

                {item.recommended_workflow_id ||
                (item.graph_message_id && onOpenInOutlook) ? (
                  <div
                    className="flex items-center gap-2"
                    data-testid="ready-brief-recommended-row"
                  >
                    {item.recommended_workflow_id ? (
                      <p className="min-w-0 flex-1 text-[10px] font-semibold uppercase tracking-[0.07em] text-primary-gray-light-500">
                        Recommended · {recommendedLabel(item.recommended_workflow_id)}
                      </p>
                    ) : null}
                    {item.graph_message_id && onOpenInOutlook ? (
                      <OpenEmailLink
                        onClick={() => onOpenInOutlook(item)}
                        testId="ready-brief-open-outlook"
                        className="ml-auto"
                      />
                    ) : null}
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={() => onOpenBrief(item)}
                  data-testid="ready-brief-open"
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-sunrise px-3 py-2.5 text-[13px] font-semibold text-primary-gray-light-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise"
                >
                  {CONTEXT_PRIMARY_ACTION.brief_ready}
                  <span aria-hidden="true">→</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default ReadyBriefsQueue;
